import React from 'react';
import TopNav from '../components/TopNav';
import Footer from '../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { CheckCircle, Shield, DollarSign, Clock, Database, Heart } from 'lucide-react';

export default function Simplified() {
  return (
    <div className="min-h-screen flex flex-col">
      <TopNav />
      
      <main className="flex-grow">
        <div className="bg-gradient-to-b from-muted/50 to-background py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <Badge className="mb-4" variant="secondary">Our Philosophy</Badge>
              <h1 className="text-5xl font-semibold tracking-tight mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                The Simplified Promise
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                At Automation Prime, every company we build, partner with, or acquire follows the same philosophy: treat customers the way we would want to be treated. We believe business should be simple, transparent, and fair.
              </p>
            </div>
            
            <Card className="rounded-xl mb-8 bg-gradient-to-br from-primary/5 to-background border-2" style={{ borderColor: 'var(--ap-blue)' }}>
              <CardContent className="pt-8 pb-8">
                <p className="text-xl leading-relaxed text-center">
                  No gimmicks. No hidden fees. No scare tactics. <br />
                  Just great products, fair pricing, and long-term relationships.
                </p>
              </CardContent>
            </Card>
            
            {/* No Contracts */}
            <Card className="rounded-xl mb-8">
              <CardHeader>
                <Shield className="h-10 w-10 mb-3" style={{ color: 'var(--ap-blue)' }} />
                <CardTitle className="text-2xl">No Contracts. Ever.</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-muted-foreground">
                <p>
                  We do not believe in locking customers into long-term contracts, cancellation penalties, or complicated exit processes.
                </p>
                <p>
                  <strong className="text-foreground">We believe customers stay because they want to, not because they are trapped.</strong> Our job is to help your business grow, and if you decide to leave, we will do everything reasonably possible to make the transition smooth.
                </p>
                <p>
                  We'll provide your data, help unwind anything we've implemented where applicable, and do our best to leave things in good shape.
                </p>
                <p className="font-semibold text-foreground">
                  No contracts. No cancellation fees. No drama. No hard feelings.
                </p>
              </CardContent>
            </Card>
            
            {/* Locked-In Pricing */}
            <Card className="rounded-xl mb-8">
              <CardHeader>
                <DollarSign className="h-10 w-10 mb-3" style={{ color: 'var(--ap-blue)' }} />
                <CardTitle className="text-2xl">Your Price Is Locked In For Life.</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-muted-foreground">
                <p>
                  The price you sign up at is the highest price you will ever pay. If our pricing increases, your rate stays the same. If our pricing decreases, we will automatically move you to the lower price.
                </p>
                <p>
                  <strong className="text-foreground">No phone call, email, or negotiation required.</strong>
                </p>
                <p className="font-semibold text-foreground">
                  Loyal customers should be rewarded, not punished.
                </p>
              </CardContent>
            </Card>
            
            {/* No Late Fees */}
            <Card className="rounded-xl mb-8">
              <CardHeader>
                <Heart className="h-10 w-10 mb-3" style={{ color: 'var(--ap-blue)' }} />
                <CardTitle className="text-2xl">No Late Fees. No Collections. No Scare Tactics.</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-muted-foreground">
                <p>
                  Life happens. Businesses go through slow periods and cash flow gets tight.
                </p>
                <p>
                  <strong className="text-foreground">If you cannot make a payment, simply reach out and let us know.</strong> We've been there before.
                </p>
                <p>
                  We never understood the logic of punishing customers who are already struggling by charging late fees, immediately shutting off services, or sending accounts to collections. That approach damages relationships and rarely benefits anyone.
                </p>
                <p>
                  Instead, when reasonable, we will work with you, help create a plan, and give you an opportunity to get back on track.
                </p>
                <p className="font-semibold text-foreground">
                  The customer receives support when they need it most, and we maintain a relationship built on trust. That's a win for everyone.
                </p>
              </CardContent>
            </Card>
            
            {/* No Implementation Fees */}
            <Card className="rounded-xl mb-8">
              <CardHeader>
                <CheckCircle className="h-10 w-10 mb-3" style={{ color: 'var(--ap-blue)' }} />
                <CardTitle className="text-2xl">No Implementation Fees. Ever.</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-muted-foreground">
                <p>
                  We will never charge implementation fees, setup fees, onboarding fees, activation fees, migration fees, or any other disguised fee designed to increase the cost of getting started.
                </p>
                <p>
                  We have seen competitors charge anywhere from $50 to over $10,000 simply to activate software that was already built, often introducing these charges at the last minute or after contracts have been signed.
                </p>
                <p>
                  <strong className="text-foreground">Modern software should be simple.</strong> Most platforms today are designed to be deployed quickly, efficiently, and with minimal effort.
                </p>
                <p className="font-semibold text-foreground">
                  The price we quote is the price you pay.
                </p>
                <p>
                  No fine print. No mandatory upgrades. No surprise invoices. No hidden costs.
                </p>
              </CardContent>
            </Card>
            
            {/* Data Ownership */}
            <Card className="rounded-xl mb-8">
              <CardHeader>
                <Database className="h-10 w-10 mb-3" style={{ color: 'var(--ap-blue)' }} />
                <CardTitle className="text-2xl">You Own Your Data.</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-muted-foreground">
                <p>
                  <strong className="text-foreground">Your data belongs to you. Always.</strong>
                </p>
                <p>
                  If you decide to leave one of our platforms, we will never hold your data hostage, charge export fees, restrict access to your information, or create unnecessary obstacles.
                </p>
                <p>
                  We will assist you in exporting your data and transitioning away from our platform whenever possible.
                </p>
              </CardContent>
            </Card>
            
            {/* Why We Operate This Way */}
            <Card className="rounded-xl mb-8 bg-muted/30">
              <CardHeader>
                <CardTitle className="text-2xl">Why We Operate This Way</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-muted-foreground">
                <p>
                  <strong className="text-foreground">Most companies optimize for short-term revenue. We optimize for long-term trust.</strong>
                </p>
                <p>
                  We believe that if we consistently provide exceptional service, fair pricing, transparency, and genuine support, customers will stay because they want to—not because they have to.
                </p>
                <p>
                  That philosophy guides every Automation Prime company today and every company we build in the future.
                </p>
              </CardContent>
            </Card>
            
            {/* Final Statement */}
            <div className="text-center">
              <Card className="rounded-xl bg-gradient-to-br from-primary/10 to-background border-2 inline-block" style={{ borderColor: 'var(--ap-blue)' }}>
                <CardContent className="pt-8 pb-8 px-12">
                  <h3 className="text-3xl font-semibold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    Simple. Fair. Transparent.
                  </h3>
                  <p className="text-xl font-medium" style={{ color: 'var(--ap-blue)' }}>
                    That's The Simplified Promise.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}
